import React from 'react';
import {
  VideoCameraIcon,
  CalendarIcon,
  ClockIcon,
  UserGroupIcon,
  CheckCircleIcon,
  DocumentTextIcon,
  ChatBubbleLeftRightIcon,
  MicrophoneIcon,
  ComputerDesktopIcon,
  UserIcon,
  PaperClipIcon,
  LinkIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline';

export default function ProjectMeetingHelp() {
  return (
    <div className="min-h-screen bg-cover bg-center bg-gradient-to-b from-indigo-300 via-sky-100 to-white w-full">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center mb-6">
            <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg">
              <VideoCameraIcon className="h-8 w-8 text-white" />
            </div>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Project Meetings</h1>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            Comprehensive meeting management system for scheduling, conducting, and tracking
            project meetings with integrated video conferencing and documentation.
          </p>
        </div>

        {/* System Overview */}
        <div className="bg-white rounded-3xl shadow-2xl border border-gray-200 overflow-hidden mb-12">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-8 py-6">
            <div className="flex items-center">
              <VideoCameraIcon className="h-8 w-8 text-white mr-4" />
              <h2 className="text-2xl font-bold text-white">Project Meeting Management Overview</h2>
            </div>
          </div>

          <div className="p-8">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-6 border border-blue-200">
                <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center mb-4">
                  <CalendarIcon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Meeting Scheduling</h3>
                <p className="text-blue-700 text-sm leading-relaxed">
                  Schedule meetings with automatic calendar integration, participant
                  invitations, and conflict detection for optimal attendance.
                </p>
              </div>

              <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-6 border border-indigo-200">
                <div className="w-12 h-12 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center mb-4">
                  <VideoCameraIcon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Video Conferencing</h3>
                <p className="text-indigo-700 text-sm leading-relaxed">
                  Built-in video conferencing with screen sharing, recording capabilities,
                  and integration with popular video platforms.
                </p>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-6 border border-purple-200">
                <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-600 rounded-xl flex items-center justify-center mb-4">
                  <DocumentTextIcon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Meeting Minutes</h3>
                <p className="text-purple-700 text-sm leading-relaxed">
                  Automatic generation of meeting minutes, action items, and follow-up
                  tasks with assignment tracking and deadline monitoring.
                </p>
              </div>

              <div className="bg-gradient-to-br from-pink-50 to-red-50 rounded-2xl p-6 border border-pink-200">
                <div className="w-12 h-12 bg-gradient-to-r from-pink-500 to-red-600 rounded-xl flex items-center justify-center mb-4">
                  <ChartBarIcon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Meeting Analytics</h3>
                <p className="text-pink-700 text-sm leading-relaxed">
                  Comprehensive analytics on meeting effectiveness, attendance patterns,
                  and productivity metrics for continuous improvement.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Interface Button Layouts */}
        <div className="bg-white rounded-3xl shadow-2xl border border-gray-200 overflow-hidden mb-12">
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-8 py-6">
            <div className="flex items-center">
              <CheckCircleIcon className="h-8 w-8 text-white mr-4" />
              <h2 className="text-2xl font-bold text-white">Interface Button Layouts</h2>
            </div>
          </div>

          <div className="p-8">
            {/* Meeting Dashboard */}
            <div className="bg-white rounded-xl p-6 mb-8 border border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Meeting Dashboard</h3>

              <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg p-6 border border-gray-200">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-6">
                  <h2 className="text-2xl font-bold text-gray-900">Project Meetings</h2>
                  <button className="inline-flex items-center gap-x-1.5 rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm opacity-75 cursor-not-allowed">
                    <CalendarIcon className="h-5 w-5" />
                    Schedule Meeting
                  </button>
                </div>

                {/* Meeting Stats */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                  <div className="bg-white rounded-lg p-4 border border-gray-200">
                    <div className="flex items-center">
                      <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center mr-3">
                        <CalendarIcon className="h-4 w-4 text-white" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Total Meetings</p>
                        <p className="text-lg font-bold text-gray-900">24</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-lg p-4 border border-gray-200">
                    <div className="flex items-center">
                      <div className="w-8 h-8 bg-green-500 rounded-lg flex items-center justify-center mr-3">
                        <CheckCircleIcon className="h-4 w-4 text-white" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Completed</p>
                        <p className="text-lg font-bold text-gray-900">18</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-lg p-4 border border-gray-200">
                    <div className="flex items-center">
                      <div className="w-8 h-8 bg-yellow-500 rounded-lg flex items-center justify-center mr-3">
                        <ClockIcon className="h-4 w-4 text-white" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Upcoming</p>
                        <p className="text-lg font-bold text-gray-900">3</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-lg p-4 border border-gray-200">
                    <div className="flex items-center">
                      <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center mr-3">
                        <UserGroupIcon className="h-4 w-4 text-white" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Avg Attendance</p>
                        <p className="text-lg font-bold text-gray-900">85%</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Meeting List */}
            <div className="bg-white rounded-xl p-6 mb-8 border border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Upcoming Meetings</h3>

              <div className="space-y-4">
                {/* Meeting Item */}
                <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h4 className="text-lg font-semibold text-gray-900">Sprint Planning Meeting</h4>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          Project Alpha
                        </span>
                      </div>

                      <div className="flex items-center space-x-4 text-sm text-gray-600 mb-3">
                        <div className="flex items-center space-x-1">
                          <CalendarIcon className="h-4 w-4" />
                          <span>Oct 15, 2025</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <ClockIcon className="h-4 w-4" />
                          <span>10:00 AM - 11:30 AM</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <VideoCameraIcon className="h-4 w-4" />
                          <span>Virtual Meeting</span>
                        </div>
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <UserGroupIcon className="h-4 w-4 text-gray-400" />
                          <span className="text-sm text-gray-600">8 participants</span>
                        </div>

                        <div className="flex space-x-2">
                          <button className="inline-flex items-center gap-x-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm opacity-75 cursor-not-allowed">
                            <VideoCameraIcon className="h-4 w-4" />
                            Join
                          </button>
                          <button className="inline-flex items-center gap-x-1.5 rounded-md bg-gray-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm opacity-75 cursor-not-allowed">
                            <DocumentTextIcon className="h-4 w-4" />
                            View Agenda
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Another Meeting Item */}
                <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h4 className="text-lg font-semibold text-gray-900">Client Review Session</h4>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Beta Project
                        </span>
                      </div>

                      <div className="flex items-center space-x-4 text-sm text-gray-600 mb-3">
                        <div className="flex items-center space-x-1">
                          <CalendarIcon className="h-4 w-4" />
                          <span>Oct 18, 2025</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <ClockIcon className="h-4 w-4" />
                          <span>2:00 PM - 3:00 PM</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <VideoCameraIcon className="h-4 w-4" />
                          <span>Conference Room A</span>
                        </div>
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <UserGroupIcon className="h-4 w-4 text-gray-400" />
                          <span className="text-sm text-gray-600">12 participants</span>
                        </div>

                        <div className="flex space-x-2">
                          <button className="inline-flex items-center gap-x-1.5 rounded-md bg-green-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm opacity-75 cursor-not-allowed">
                            <VideoCameraIcon className="h-4 w-4" />
                            Join
                          </button>
                          <button className="inline-flex items-center gap-x-1.5 rounded-md bg-gray-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm opacity-75 cursor-not-allowed">
                            <DocumentTextIcon className="h-4 w-4" />
                            View Minutes
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Meeting Controls */}
            <div className="bg-white rounded-xl p-6 mb-8 border border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Meeting Controls</h3>

              <div className="bg-white rounded-lg p-4 border border-gray-200">
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 space-x-4">
                  <button className="inline-flex items-center gap-x-1.5 rounded-md bg-indigo-600 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm opacity-75 cursor-not-allowed">
                    <VideoCameraIcon className="h-5 w-5" />
                    Start Meeting
                  </button>

                  <button className="inline-flex items-center gap-x-1.5 rounded-md bg-blue-600 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm opacity-75 cursor-not-allowed">
                    <MicrophoneIcon className="h-5 w-5" />
                    Audio Settings
                  </button>

                  <button className="inline-flex items-center gap-x-1.5 rounded-md bg-green-600 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm opacity-75 cursor-not-allowed">
                    <ComputerDesktopIcon className="h-5 w-5" />
                    Screen Share
                  </button>

                  <button className="inline-flex items-center gap-x-1.5 rounded-md bg-purple-600 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm opacity-75 cursor-not-allowed">
                    <DocumentTextIcon className="h-5 w-5" />
                    Record Meeting
                  </button>
                </div>
              </div>
            </div>

            {/* Meeting Minutes Interface */}
            <div className="bg-white rounded-xl p-6 mb-8 border border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Meeting Minutes & Actions</h3>

              <div className="bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200">
                <div className="bg-gradient-to-r from-green-600 to-blue-600 px-6 py-4">
                  <h2 className="text-xl font-bold text-white">Meeting Minutes - Sprint Planning</h2>
                  <p className="text-green-100 text-sm">October 10, 2025 • 10:00 AM - 11:30 AM</p>
                </div>

                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Attendees */}
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Attendees</h3>
                      <div className="space-y-2">
                        <div className="flex items-center space-x-2">
                          <UserIcon className="h-4 w-4 text-gray-400" />
                          <span className="text-sm text-gray-700">John Smith (Scrum Master)</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <UserIcon className="h-4 w-4 text-gray-400" />
                          <span className="text-sm text-gray-700">Sarah Johnson (Developer)</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <UserIcon className="h-4 w-4 text-gray-400" />
                          <span className="text-sm text-gray-700">Mike Davis (QA Lead)</span>
                        </div>
                      </div>
                    </div>

                    {/* Action Items */}
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Action Items</h3>
                      <div className="space-y-3">
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <p className="text-sm font-medium text-yellow-900">Update sprint backlog</p>
                              <p className="text-xs text-yellow-700 mt-1">Add new user stories to the backlog</p>
                            </div>
                            <div className="text-right">
                              <span className="text-xs text-yellow-600">Sarah Johnson</span>
                              <p className="text-xs text-yellow-600">Due: Oct 12</p>
                            </div>
                          </div>
                        </div>

                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <p className="text-sm font-medium text-blue-900">Setup CI/CD pipeline</p>
                              <p className="text-xs text-blue-700 mt-1">Configure automated deployment</p>
                            </div>
                            <div className="text-right">
                              <span className="text-xs text-blue-600">Mike Davis</span>
                              <p className="text-xs text-blue-600">Due: Oct 15</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Meeting Notes */}
                  <div className="mt-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Meeting Notes</h3>
                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <p className="text-sm text-gray-700 leading-relaxed">
                        Discussed sprint goals and priorities. Team agreed on 8 user stories for the upcoming sprint.
                        Identified potential blockers related to third-party API integration. Action items assigned
                        to respective team members with clear deadlines.
                      </p>
                    </div>
                  </div>

                  {/* Action buttons */}
                  <div className="flex justify-end space-x-3 mt-6">
                    <button className="inline-flex items-center gap-x-1.5 rounded-md bg-gray-600 px-3 py-2 text-sm font-semibold text-white shadow-sm opacity-75 cursor-not-allowed">
                      <PaperClipIcon className="h-4 w-4" />
                      Attach Files
                    </button>
                    <button className="inline-flex items-center gap-x-1.5 rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm opacity-75 cursor-not-allowed">
                      <LinkIcon className="h-4 w-4" />
                      Share Link
                    </button>
                    <button className="inline-flex items-center gap-x-1.5 rounded-md bg-green-600 px-3 py-2 text-sm font-semibold text-white shadow-sm opacity-75 cursor-not-allowed">
                      <CheckCircleIcon className="h-4 w-4" />
                      Mark Complete
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Feature Explanations */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
              <div className="bg-white rounded-xl p-6 border border-blue-200 shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center mr-3">
                    <span className="text-white font-bold text-sm">1</span>
                  </div>
                  <h4 className="text-lg font-bold text-blue-900">Smart Scheduling</h4>
                </div>
                <p className="text-blue-700 text-sm leading-relaxed">
                  Intelligent meeting scheduling with automatic conflict detection,
                  optimal time suggestions, and calendar integration across all participants.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-indigo-200 shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-indigo-500 rounded-lg flex items-center justify-center mr-3">
                    <span className="text-white font-bold text-sm">2</span>
                  </div>
                  <h4 className="text-lg font-bold text-indigo-900">Integrated Video</h4>
                </div>
                <p className="text-indigo-700 text-sm leading-relaxed">
                  Built-in video conferencing with HD quality, screen sharing,
                  recording capabilities, and seamless integration with meeting workflows.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-purple-200 shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center mr-3">
                    <span className="text-white font-bold text-sm">3</span>
                  </div>
                  <h4 className="text-lg font-bold text-purple-900">AI-Powered Minutes</h4>
                </div>
                <p className="text-purple-700 text-sm leading-relaxed">
                  Automatic generation of meeting minutes, action item extraction,
                  and intelligent summarization to capture key decisions and outcomes.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-pink-200 shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-pink-500 rounded-lg flex items-center justify-center mr-3">
                    <span className="text-white font-bold text-sm">4</span>
                  </div>
                  <h4 className="text-lg font-bold text-pink-900">Action Tracking</h4>
                </div>
                <p className="text-pink-700 text-sm leading-relaxed">
                  Automated follow-up on action items with deadline tracking,
                  progress monitoring, and automatic reminders to ensure completion.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-emerald-200 shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-emerald-500 rounded-lg flex items-center justify-center mr-3">
                    <span className="text-white font-bold text-sm">5</span>
                  </div>
                  <h4 className="text-lg font-bold text-emerald-900">Meeting Analytics</h4>
                </div>
                <p className="text-emerald-700 text-sm leading-relaxed">
                  Comprehensive analytics on meeting effectiveness, attendance patterns,
                  and productivity metrics to optimize future meeting planning.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-yellow-200 shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-yellow-500 rounded-lg flex items-center justify-center mr-3">
                    <span className="text-white font-bold text-sm">6</span>
                  </div>
                  <h4 className="text-lg font-bold text-yellow-900">Resource Integration</h4>
                </div>
                <p className="text-yellow-700 text-sm leading-relaxed">
                  Seamless integration with project documents, shared drives,
                  and collaboration tools for comprehensive meeting preparation and follow-up.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Best Practices */}
        <div className="bg-white rounded-3xl shadow-2xl border border-gray-200 overflow-hidden">
          <div className="bg-gradient-to-r from-purple-600 to-pink-600 px-8 py-6">
            <div className="flex items-center">
              <CheckCircleIcon className="h-8 w-8 text-white mr-4" />
              <h2 className="text-2xl font-bold text-white">Project Meeting Best Practices</h2>
            </div>
          </div>

          <div className="p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-6">
                <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
                  <h3 className="text-lg font-bold text-blue-900 mb-3">Meeting Preparation</h3>
                  <ul className="space-y-2 text-sm text-blue-700">
                    <li>• Set clear objectives and agenda in advance</li>
                    <li>• Share relevant documents and background materials</li>
                    <li>• Ensure all participants understand the meeting purpose</li>
                    <li>• Test video/audio equipment before starting</li>
                    <li>• Prepare action items and decision points</li>
                  </ul>
                </div>

                <div className="bg-indigo-50 rounded-xl p-6 border border-indigo-200">
                  <h3 className="text-lg font-bold text-indigo-900 mb-3">During the Meeting</h3>
                  <ul className="space-y-2 text-sm text-indigo-700">
                    <li>• Start and end meetings on time</li>
                    <li>• Keep discussions focused on agenda items</li>
                    <li>• Encourage participation from all attendees</li>
                    <li>• Use screen sharing effectively for demonstrations</li>
                    <li>• Take notes and capture key decisions</li>
                  </ul>
                </div>
              </div>

              <div className="space-y-6">
                <div className="bg-purple-50 rounded-xl p-6 border border-purple-200">
                  <h3 className="text-lg font-bold text-purple-900 mb-3">Follow-up Actions</h3>
                  <ul className="space-y-2 text-sm text-purple-700">
                    <li>• Distribute meeting minutes within 24 hours</li>
                    <li>• Assign action items with clear deadlines</li>
                    <li>• Track progress on assigned tasks</li>
                    <li>• Schedule follow-up meetings as needed</li>
                    <li>• Update project documentation</li>
                  </ul>
                </div>

                <div className="bg-pink-50 rounded-xl p-6 border border-pink-200">
                  <h3 className="text-lg font-bold text-pink-900 mb-3">Continuous Improvement</h3>
                  <ul className="space-y-2 text-sm text-pink-700">
                    <li>• Review meeting effectiveness regularly</li>
                    <li>• Analyze attendance and participation patterns</li>
                    <li>• Optimize meeting frequency and duration</li>
                    <li>• Incorporate feedback from participants</li>
                    <li>• Use analytics to improve meeting quality</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}